/**
 * Translation telemetry
 * Tracks per-language metrics:
 *  - chunk count, item count, latency (avg, p50, p95)
 *  - cache hits / misses (hit rate)
 *  - errors
 * Persisted to localStorage so the admin diagnostics tab can read history.
 *
 * Also emits a custom DOM event "translate:state" for UI overlays.
 */

export type TranslateState = "idle" | "loading" | "done" | "error";

type LangStats = {
  lang: string;
  chunks: number;
  items: number;
  cacheHits: number;
  cacheMisses: number;
  errors: number;
  latencies: number[]; // ms per chunk (capped)
  lastUpdated: number;
};

const STORAGE_KEY = "translate_telemetry_v1";
const MAX_LATENCY_SAMPLES = 200;

const loadAll = (): Record<string, LangStats> => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return {};
};

const saveAll = (data: Record<string, LangStats>) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {}
};

const ensure = (data: Record<string, LangStats>, lang: string): LangStats => {
  if (!data[lang]) {
    data[lang] = {
      lang,
      chunks: 0,
      items: 0,
      cacheHits: 0,
      cacheMisses: 0,
      errors: 0,
      latencies: [],
      lastUpdated: Date.now(),
    };
  }
  return data[lang];
};

export const recordChunk = (
  lang: string,
  itemCount: number,
  latencyMs: number,
  ok: boolean
) => {
  const all = loadAll();
  const s = ensure(all, lang);
  s.chunks += 1;
  s.items += itemCount;
  if (ok) {
    s.latencies.push(Math.round(latencyMs));
    if (s.latencies.length > MAX_LATENCY_SAMPLES) {
      s.latencies = s.latencies.slice(-MAX_LATENCY_SAMPLES);
    }
  } else {
    s.errors += 1;
  }
  s.lastUpdated = Date.now();
  saveAll(all);
};

export const recordCache = (lang: string, hits: number, misses: number) => {
  if (hits === 0 && misses === 0) return;
  const all = loadAll();
  const s = ensure(all, lang);
  s.cacheHits += hits;
  s.cacheMisses += misses;
  s.lastUpdated = Date.now();
  saveAll(all);
};

export type DerivedStats = LangStats & {
  hitRate: number; // 0..1
  avgLatency: number;
  p50: number;
  p95: number;
};

const percentile = (arr: number[], p: number): number => {
  if (arr.length === 0) return 0;
  const sorted = [...arr].sort((a, b) => a - b);
  const idx = Math.min(sorted.length - 1, Math.floor((p / 100) * sorted.length));
  return sorted[idx];
};

export const getDerivedStats = (): DerivedStats[] => {
  const all = loadAll();
  return Object.values(all).map((s) => {
    const total = s.cacheHits + s.cacheMisses;
    const hitRate = total > 0 ? s.cacheHits / total : 0;
    const avg =
      s.latencies.length > 0
        ? s.latencies.reduce((a, b) => a + b, 0) / s.latencies.length
        : 0;
    return {
      ...s,
      hitRate,
      avgLatency: Math.round(avg),
      p50: percentile(s.latencies, 50),
      p95: percentile(s.latencies, 95),
    };
  });
};

export const clearTelemetry = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {}
};

// ---- UI state events ----
export type TranslateProgress = { done: number; total: number };

export const emitState = (
  lang: string,
  state: TranslateState,
  progress?: TranslateProgress
) => {
  window.dispatchEvent(
    new CustomEvent("translate:state", { detail: { lang, state, progress } })
  );
};
